<template>
  <div class="user-center">
    <!-- 左侧个人信息栏 -->
    <aside class="uc-sidebar">
      <div class="uc-profile-section">
        <header class="uc-profile-header">
          <h2>个人信息</h2>
        </header>

        <div v-if="loading" class="uc-skeleton-loader">
          <div class="uc-skeleton uc-skeleton-avatar"></div>
          <div class="uc-skeleton uc-skeleton-text uc-skeleton-text-lg"></div>
          <div class="uc-skeleton uc-skeleton-text uc-skeleton-text-md"></div>
          <div class="uc-skeleton uc-skeleton-text uc-skeleton-text-sm"></div>
        </div>

        <div v-else class="uc-profile-content">
          <div class="uc-avatar-section">
            <img
              :src="getAvatarUrl(userInfo.avatarUrl)"
              :alt="userInfo.nickname"
              class="uc-profile-avatar"
              @error="handleAvatarError"
            />
          </div>

          <div class="uc-info-section">
            <div class="uc-info-item">
              <label class="uc-info-label">
                <i class="el-icon-user"></i>
                昵称
              </label>
              <div class="uc-info-value">{{ userInfo.nickname || "用户" }}</div>
            </div>

            <div class="uc-info-item">
              <label class="uc-info-label">
                <i class="el-icon-phone"></i>
                手机号
              </label>
              <div class="uc-info-value">
                {{ userInfo.phone || "未设置手机号" }}
              </div>
            </div>

            <!-- 修改密码按钮 -->
            <div class="uc-action-section">
              <button
                class="uc-change-password-btn"
                @click="showChangePasswordDialog"
              >
                <i class="el-icon-key"></i>
                修改密码
              </button>
            </div>
          </div>
        </div>
      </div>
    </aside>

    <!-- 右侧订单内容区 -->
    <main class="uc-main-content">
      <section class="uc-orders-section">
        <header class="uc-section-header">
          <h2>我的订单</h2>
        </header>

        <!-- 订单标签页 -->
        <div class="uc-tab-container">
          <div
            v-for="tab in orderTabs"
            :key="tab.key"
            class="uc-tab-item"
            :class="{ 'uc-active': orderTab === tab.key }"
            @click="switchOrderTab(tab.key)"
          >
            {{ tab.label }}
          </div>
        </div>

        <!-- 订单内容 -->
        <div v-if="ordersLoading" class="uc-skeleton-orders">
          <div v-for="i in 3" :key="i" class="uc-skeleton-order-card">
            <div class="uc-skeleton uc-skeleton-text uc-skeleton-text-md"></div>
            <div class="uc-skeleton uc-skeleton-text uc-skeleton-text-lg"></div>
            <div class="uc-skeleton uc-skeleton-text uc-skeleton-text-sm"></div>
          </div>
        </div>

        <div v-else-if="filteredOrders.length === 0" class="uc-empty-state">
          <i class="el-icon-document-remove"></i>
          <p>
            {{
              orderTab === "all"
                ? "暂无订单记录"
                : `暂无${getCurrentTabLabel()}订单`
            }}
          </p>
        </div>

        <div v-else class="uc-orders-list">
          <div
            v-for="order in filteredOrders"
            :key="order.id"
            class="uc-order-card"
          >
            <div class="uc-order-header">
              <span class="uc-order-no">订单号: {{ order.orderNo }}</span>
              <span
                class="uc-order-status"
                :class="`uc-status-${
                  getOrderStatusInfo(order.orderStatus).status
                }`"
                :style="{
                  backgroundColor: getOrderStatusInfo(order.orderStatus).color,
                }"
              >
                {{ getOrderStatusInfo(order.orderStatus).label }}
              </span>
            </div>

            <div class="uc-order-body">
              <div class="uc-service-icon">
                <i class="el-icon-s-custom"></i>
              </div>

              <div class="uc-order-details">
                <h4 class="uc-order-title">{{ order.orderName }}</h4>
                <p class="uc-order-desc">{{ order.orderDetails }}</p>
                <div class="uc-order-meta">
                  <span class="uc-service-type">{{ order.serviceType }}</span>
                  <span class="uc-merchant">{{
                    order.selectMerchantName
                  }}</span>
                  <span class="uc-order-amount"
                    >¥{{ formatAmount(order.actualAmount) }}</span
                  >
                </div>
                <div class="uc-appointment-info" v-if="order.appointmentDate">
                  <i class="el-icon-time"></i>
                  <span
                    >{{ order.appointmentDate }}
                    {{ formatTime(order.appointmentTime) }}</span
                  >
                </div>

                <!-- 申诉状态显示 -->
                <div class="uc-appeal-info" v-if="order.appealStatus === '1'">
                  <i class="el-icon-warning"></i>
                  <span class="appeal-text">申诉处理中</span>
                </div>

                <div
                  class="uc-appeal-info"
                  v-if="order.appealStatus === '2' && order.appealResult"
                >
                  <i class="el-icon-circle-check"></i>
                  <span class="appeal-result"
                    >申诉结果：{{ order.appealResult }}</span
                  >
                </div>

                <!-- 退款原因显示 -->
                <div class="uc-refund-info" v-if="order.refundReason">
                  <i class="el-icon-remove"></i>
                  <span>退款原因：{{ order.refundReason }}</span>
                </div>
              </div>
            </div>

            <div class="uc-order-footer">
              <span class="uc-order-time">{{ order.createTime }}</span>
            </div>
          </div>
        </div>

        <!-- 分页组件 -->
        <div
          v-if="!ordersLoading && filteredOrders.length > 0"
          class="uc-pagination"
        >
          <el-pagination
            background
            layout="prev, pager, next, jumper, total"
            :current-page="pagination.current"
            :page-size="pagination.size"
            :total="pagination.total"
            @current-change="handlePageChange"
            small
          />
        </div>
      </section>
    </main>

    <!-- 修改密码对话框 -->
    <el-dialog
      title="修改密码"
      :visible.sync="changePasswordDialog"
      width="500px"
      :close-on-click-modal="false"
      @close="resetPasswordForm"
      custom-class="force-password-change-dialog"
    >
      <div class="password-change-header user-id-notice">
        <i class="el-icon-warning"></i>
        <p class="password-change-title">
          请设置新的登录密码，密码长度为6-20位。<br />
          请在"三火私服"小程序中查看并填写您的用户ID进行身份验证。
        </p>
      </div>

      <el-form
        ref="passwordForm"
        :model="passwordForm"
        :rules="passwordRules"
        label-width="80px"
        class="password-change-form"
      >
        <el-form-item label="新密码" prop="newPassword">
          <el-input
            v-model="passwordForm.newPassword"
            type="password"
            placeholder="请输入新密码（6-20位）"
            show-password
          />
        </el-form-item>

        <el-form-item label="确认密码" prop="confirmPassword">
          <el-input
            v-model="passwordForm.confirmPassword"
            type="password"
            placeholder="请再次输入新密码"
            show-password
            @keyup.enter.native="handleChangePassword"
          />
        </el-form-item>
        <el-form-item label="用户ID" prop="userId">
          <el-input
            v-model="passwordForm.userId"
            type="text"
            placeholder="请输入您的用户ID进行身份验证"
          />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="changePasswordDialog = false">取消</el-button>
        <el-button
          type="primary"
          @click="handleChangePassword"
          :loading="passwordLoading"
          class="password-change-btn"
        >
          确认修改
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { getImageUrl } from "@/utils/imageUtils";
import { userAPI } from "@/api";

export default {
  name: "UserCenter",
  data() {
    return {
      loading: true,
      ordersLoading: true,
      orderTab: "all",
      orders: [],
      defaultAvatar: require("@/assets/logo.png"),

      // 分页参数
      pagination: {
        current: 1, // 当前页码
        size: 10, // 每页数量
        total: 0, // 总记录数
        pages: 0, // 总页数
      },

      // 查询参数
      queryParams: {
        orderStatus: "", // 订单状态 (1-6对应不同状态)
        orderName: "", // 搜索的订单名称
        timeSort: "1", // 时间排序方式
      },

      // 订单标签配置
      orderTabs: [
        { key: "all", label: "全部订单" },
        { key: "1", label: "待服务" },
        { key: "2", label: "服务中" },
        { key: "3", label: "待评价" },
        { key: "4", label: "已完成" },
        { key: "5,6", label: "已取消" },
      ],

      // 订单状态映射
      orderStatusMap: {
        1: { label: "待服务", color: "#2485D5", status: "pending" },
        2: { label: "服务中", color: "#F5BD00", status: "processing" },
        3: { label: "待评价", color: "#FF4840", status: "review" },
        4: { label: "已完成", color: "#00d3b8", status: "completed" },
        5: { label: "退单中", color: "#ff4840", status: "refunding" },
        6: { label: "已退单", color: "#00d3b8", status: "refunded" },
      },

      // 修改密码相关
      changePasswordDialog: false,
      passwordLoading: false,
      passwordForm: {
        userId: "",
        newPassword: "",
        confirmPassword: "",
      },
    };
  },

  computed: {
    ...mapState("user", ["userInfo"]),

    // 密码验证规则
    passwordRules() {
      return {
        userId: [
          { required: true, message: "请输入用户ID", trigger: "blur" },
          { min: 1, message: "用户ID不能为空", trigger: "blur" },
        ],
        newPassword: [
          { required: true, message: "请输入新密码", trigger: "blur" },
          { min: 6, max: 20, message: "密码长度为6-20位", trigger: "blur" },
          { validator: this.validateNewPassword, trigger: "blur" },
        ],
        confirmPassword: [
          { required: true, message: "请再次输入新密码", trigger: "blur" },
          { validator: this.validateConfirmPassword, trigger: "blur" },
        ],
      };
    },

    // 过滤后的订单列表（现在直接返回orders，因为后端已经过滤）
    filteredOrders() {
      return this.orders;
    },
  },

  created() {
    this.initData();
  },

  methods: {
    // 获取用户头像URL
    getAvatarUrl(avatar) {
      if (!avatar) {
        return this.defaultAvatar;
      }
      return getImageUrl(avatar);
    },

    // 显示修改密码对话框
    showChangePasswordDialog() {
      this.changePasswordDialog = true;
    },

    // 重置密码表单
    resetPasswordForm() {
      this.passwordForm = {
        userId: "",
        newPassword: "",
        confirmPassword: "",
      };
      if (this.$refs.passwordForm) {
        this.$refs.passwordForm.clearValidate();
      }
    },

    // 确认密码验证
    validateConfirmPassword(rule, value, callback) {
      if (value !== this.passwordForm.newPassword) {
        callback(new Error("两次输入的密码不一致"));
      } else {
        callback();
      }
    },

    // 验证新密码（不能与手机号相同）
    validateNewPassword(rule, value, callback) {
      const userPhone = this.userInfo?.phone || this.userInfo?.mobile;

      if (value && userPhone && value === userPhone) {
        callback(new Error("新密码不能与手机号相同"));
      } else {
        callback();
      }
    },

    // 处理修改密码
    async handleChangePassword() {
      try {
        // 表单验证
        await this.$refs.passwordForm.validate();

        // 验证用户ID是否匹配
        const inputUserId = this.passwordForm.userId.trim();
        const currentUserId = this.userInfo.userId || this.userInfo.id;

        if (inputUserId !== currentUserId) {
          this.$msg.error("用户ID不匹配，请输入正确的用户ID");
          return;
        }

        this.passwordLoading = true;

        // 构建用户数据对象，基于当前用户信息
        const userUpdateData = {
          userId: this.userInfo.userId || this.userInfo.id,
          username: this.userInfo.username,
          nickname: this.userInfo.nickname,
          email: "已修改", // 修改密码后，邮箱标记为"已修改"
          phone: undefined, // 设置为 undefined，不修改手机号
          password: this.passwordForm.newPassword, // 新密码
        };

        // 调用用户更新接口
        const response = await userAPI.putObj(userUpdateData);

        console.log("密码修改API调用结果:", response);
        console.log("发送的用户数据:", userUpdateData);

        if (response && response.code === 0) {
          // 更新本地用户信息状态
          this.$store.commit("user/UPDATE_USER_INFO", {
            ...this.userInfo,
            email: "已修改",
          });

          console.log("本地用户信息已更新，当前email:", this.userInfo.email);

          this.$msg.success("密码修改成功，请重新登录");

          // 重置表单
          this.resetPasswordForm();
          this.changePasswordDialog = false;

          // 延迟后退出登录
          setTimeout(() => {
            this.$store.dispatch("user/logout");
            this.$router.push("/");
          }, 1500);
        } else {
          this.$msg.error(response?.msg || "密码修改失败");
        }
      } catch (error) {
        console.error("修改密码失败:", error);
        if (error.response) {
          // API返回的错误
          this.$msg.error(error.response.data?.msg || "密码修改失败");
        } else {
          // 表单验证错误或其他错误
          this.$msg.error("请检查输入信息");
        }
      } finally {
        this.passwordLoading = false;
      }
    },

    // 初始化数据
    async initData() {
      this.loading = true;
      this.ordersLoading = true;

      try {
        // 模拟加载延迟
        await Promise.all([this.loadUserInfo(), this.loadOrders()]);
      } catch (error) {
        console.error("数据加载失败:", error);
        this.$msg.error("数据加载失败，请刷新页面重试");
      } finally {
        this.loading = false;
        this.ordersLoading = false;
      }
    },

    // 加载用户信息
    async loadUserInfo() {
      await new Promise((resolve) => setTimeout(resolve, 800));
      // 用户信息已经在登录时获取并存储，这里不需要额外操作

      // 检查是否需要修改密码
      this.checkPasswordChangeRequired();
    },

    // 检查是否需要修改密码
    checkPasswordChangeRequired() {
      // 如果邮箱为空、null、undefined或只包含空白字符，说明从未修改过密码，弹出提示
      const email = this.userInfo.email;
      console.log(
        "检查密码修改状态，当前邮箱字段:",
        email,
        "类型:",
        typeof email,
        "长度:",
        email ? email.length : "无长度"
      );

      // 检查各种空值情况
      const isNull = email === null;
      const isUndefined = email === undefined;
      const isEmpty = email === "";
      const isWhitespace = typeof email === "string" && email.trim() === "";

      console.log("检查结果:", {
        isNull,
        isUndefined,
        isEmpty,
        isWhitespace,
        shouldShowWarning: isNull || isUndefined || isEmpty || isWhitespace,
      });

      if (
        email === null ||
        email === undefined ||
        email === "" ||
        (typeof email === "string" && email.trim() === "")
      ) {
        console.log("检测到用户未修改过密码，显示提示");
        this.$nextTick(() => {
          this.$msg({
            message: "为了您的账户安全，建议您修改初始密码",
            type: "warning",
            duration: 5000,
            showClose: true,
          });
        });
      } else {
        console.log("用户已修改过密码，邮箱字段值:", email);
      }
    },

    // 加载订单数据
    async loadOrders() {
      try {
        this.ordersLoading = true;

        // 构建请求参数
        const requestParams = {
          current: this.pagination.current,
          size: this.pagination.size,
          orderName: this.queryParams.orderName,
          timeSort: this.queryParams.timeSort,
        };

        // 根据选中的标签设置订单状态
        if (this.orderTab !== "all") {
          if (this.orderTab === "5,6") {
            // 特殊处理取消状态，需要分别查询
            // 这里我们先查询状态5，后续可以优化为一次查询
            requestParams.orderStatus = "5";
          } else {
            requestParams.orderStatus = this.orderTab;
          }
        }

        // 如果当前用户已登录，添加用户ID参数
        if (this.userInfo.userId) {
          requestParams.createId = this.userInfo.userId;
        }

        console.log("请求订单数据参数:", requestParams);

        // 调用API获取订单数据
        const response = await userAPI.getOrdersPage(requestParams);

        if (response && response.code === 0 && response.data) {
          // 更新订单列表
          this.orders = response.data.records || [];

          // 更新分页信息
          this.pagination = {
            current: response.data.current || 1,
            size: response.data.size || 10,
            total: response.data.total || 0,
            pages: response.data.pages || 0,
          };

          console.log("订单数据加载成功:", {
            订单数量: this.orders.length,
            分页信息: this.pagination,
          });

          // 如果是取消状态，还需要查询状态6的数据
          if (this.orderTab === "5,6" && requestParams.orderStatus === "5") {
            try {
              const canceledParams = { ...requestParams, orderStatus: "6" };
              const canceledResponse = await userAPI.getOrdersPage(
                canceledParams
              );

              if (
                canceledResponse &&
                canceledResponse.code === 0 &&
                canceledResponse.data
              ) {
                // 合并已退单的订单
                this.orders = [
                  ...this.orders,
                  ...(canceledResponse.data.records || []),
                ];

                // 更新总数
                this.pagination.total += canceledResponse.data.total || 0;
              }
            } catch (error) {
              console.warn("获取已退单订单失败:", error);
            }
          }
        } else {
          throw new Error(response?.msg || "获取订单数据失败");
        }
      } catch (error) {
        console.error("订单数据加载失败:", error);
        this.$msg.error("订单数据加载失败：" + (error.message || "请稍后重试"));

        // 设置空数据
        this.orders = [];
        this.pagination = {
          current: 1,
          size: 10,
          total: 0,
          pages: 0,
        };
      } finally {
        this.ordersLoading = false;
      }
    },

    // 切换订单标签
    switchOrderTab(tabKey) {
      if (this.orderTab !== tabKey) {
        this.orderTab = tabKey;
        // 重置分页
        this.pagination.current = 1;
        // 重新加载订单数据
        this.loadOrders();
      }
    },

    // 处理分页变化
    handlePageChange(page) {
      this.pagination.current = page;
      this.loadOrders();
    },

    // 获取当前标签名称
    getCurrentTabLabel() {
      const tab = this.orderTabs.find((t) => t.key === this.orderTab);
      return tab ? tab.label : "订单";
    },

    // 获取订单状态信息
    getOrderStatusInfo(orderStatus) {
      return (
        this.orderStatusMap[orderStatus] || {
          label: "未知状态",
          color: "#666",
          status: "unknown",
        }
      );
    },

    // 格式化金额显示
    formatAmount(amount) {
      const num = parseFloat(amount);
      return num.toFixed(2);
    },

    // 格式化时间显示
    formatTime(timeStr) {
      if (!timeStr) return "";
      return timeStr.replace(/:\d{2}$/, ""); // 移除秒数，只显示时分
    },

    // 处理头像加载错误
    handleAvatarError(event) {
      event.target.src = this.defaultAvatar;
    },

    // 处理图片加载错误
    handleImageError(event) {
      event.target.src = this.defaultAvatar;
    },
  },
};
</script>

<style lang="scss" scoped>
.user-center {
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: 20px;
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  background: linear-gradient(135deg, #fef7f7 0%, #fef1f1 50%, #fdeded 100%);
  min-height: calc(100vh - 64px);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    "Helvetica Neue", Arial, sans-serif;
}

// 左侧个人信息栏样式
.uc-sidebar {
  .uc-profile-section {
    background: #ffffff;
    border-radius: 12px;
    box-shadow: 0 2px 12px rgba(229, 62, 62, 0.08);
    border: 2px solid #fed7d7;
    overflow: hidden;
    transition: all 0.3s ease;

    &:hover {
      box-shadow: 0 4px 20px rgba(229, 62, 62, 0.12);
      border-color: #feb2b2;
    }

    .uc-profile-header {
      background: linear-gradient(135deg, #e53e3e 0%, #c53030 100%);
      color: white;
      padding: 16px 20px;
      text-align: center;
      position: relative;

      &::after {
        content: "";
        position: absolute;
        bottom: 0;
        left: 50%;
        transform: translateX(-50%);
        width: 60px;
        height: 3px;
        background: rgba(255, 255, 255, 0.8);
        border-radius: 2px;
      }

      h2 {
        font-size: 16px;
        font-weight: 600;
        margin: 0;
        letter-spacing: 0.5px;
      }
    }

    .uc-profile-content {
      padding: 20px 16px;

      .uc-avatar-section {
        text-align: center;
        margin-bottom: 20px;
        position: relative;

        .uc-profile-avatar {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          object-fit: cover;
          border: 3px solid #e53e3e;
          box-shadow: 0 4px 12px rgba(229, 62, 62, 0.2);
          transition: all 0.3s ease;
          position: relative;
          margin: 0 auto;
          display: block;

          &::after {
            content: "";
            position: absolute;
            inset: -3px;
            border-radius: 50%;
            background: linear-gradient(45deg, #e53e3e, #fc8181, #e53e3e);
            z-index: -1;
            animation: rotate 3s linear infinite;
          }

          &:hover {
            transform: scale(1.05);
            box-shadow: 0 6px 16px rgba(229, 62, 62, 0.25);
          }
        }
      }

      .uc-info-section {
        display: flex;
        flex-direction: column;
        gap: 0;

        .uc-info-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 0;
          border-bottom: 1px solid #ffebee;
          transition: all 0.2s ease;

          &:last-child {
            border-bottom: none;
          }

          &:hover {
            background: linear-gradient(
              90deg,
              rgba(229, 62, 62, 0.03) 0%,
              transparent 100%
            );
            padding-left: 8px;
            border-radius: 6px;
          }

          .uc-info-label {
            display: flex;
            align-items: center;
            gap: 8px;
            color: #666;
            font-size: 13px;
            font-weight: 500;
            min-width: 80px;

            i {
              color: #e53e3e;
              font-size: 16px;
              width: 18px;
              text-align: center;
              padding: 2px;
              border-radius: 4px;
              background: rgba(229, 62, 62, 0.1);
            }
          }

          .uc-info-value {
            color: #1e293b;
            font-size: 14px;
            font-weight: 600;
            text-align: right;
            flex: 1;
            padding-left: 12px;
            word-break: break-all;
          }
        }
      }

      // 修改密码按钮区域
      .uc-action-section {
        margin-top: 16px;
        padding-top: 16px;
        border-top: 1px solid #ffebee;

        .uc-change-password-btn {
          width: 100%;
          padding: 12px 16px;
          background: linear-gradient(135deg, #e53e3e 0%, #c53030 100%);
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;

          &:hover {
            background: linear-gradient(135deg, #c53030 0%, #9c2626 100%);
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(229, 62, 62, 0.3);
          }

          &:active {
            transform: translateY(0);
            box-shadow: 0 2px 6px rgba(229, 62, 62, 0.2);
          }

          i {
            font-size: 16px;
          }
        }
      }
    }

    .uc-skeleton-loader {
      padding: 20px 16px;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;

      .uc-skeleton-avatar {
        width: 80px;
        height: 80px;
        border-radius: 50%;
      }

      .uc-skeleton-text {
        height: 14px;
        border-radius: 6px;
        width: 100%;

        &.uc-skeleton-text-lg {
          height: 16px;
          width: 70%;
        }

        &.uc-skeleton-text-md {
          height: 15px;
          width: 60%;
        }

        &.uc-skeleton-text-sm {
          height: 14px;
          width: 50%;
        }
      }
    }
  }
}

// 右侧订单内容区样式
.uc-main-content {
  .uc-orders-section {
    background: #ffffff;
    border-radius: 16px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    border: 1px solid #e5e7eb;
    overflow: hidden;

    .uc-section-header {
      background: linear-gradient(135deg, #e53e3e 0%, #c53030 100%);
      color: white;
      padding: 16px 20px;
      text-align: center;
      position: relative;

      &::after {
        content: "";
        position: absolute;
        bottom: 0;
        left: 50%;
        transform: translateX(-50%);
        width: 60px;
        height: 3px;
        background: rgba(255, 255, 255, 0.8);
        border-radius: 2px;
      }

      h2 {
        font-size: 16px;
        font-weight: 600;
        margin: 0;
        letter-spacing: 0.5px;
      }
    }
  }
}

// 订单标签页样式
.uc-tab-container {
  display: flex;
  background: #f8f9fa;
  margin: 0;
  border-bottom: 1px solid #e2e8f0;

  .uc-tab-item {
    flex: 1;
    padding: 14px 16px;
    text-align: center;
    cursor: pointer;
    color: #64748b;
    border-right: 1px solid #e2e8f0;
    font-size: 13px;
    font-weight: 500;
    transition: all 0.3s ease;
    position: relative;
    background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);

    &:last-child {
      border-right: none;
    }

    &:hover {
      color: #e53e3e;
      background: linear-gradient(135deg, #ffffff 0%, #fef5f5 100%);
      transform: translateY(-1px);
      box-shadow: 0 2px 4px rgba(229, 62, 62, 0.1);
    }

    &.uc-active {
      background: linear-gradient(135deg, #ffffff 0%, #fef5f5 100%);
      color: #e53e3e;
      position: relative;
      box-shadow: 0 2px 8px rgba(229, 62, 62, 0.15);
      border-color: #e53e3e;
      font-weight: 600;

      &::after {
        content: "";
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        height: 3px;
        background: linear-gradient(90deg, #e53e3e 0%, #c53030 100%);
      }

      &::before {
        content: "";
        position: absolute;
        top: 4px;
        left: 50%;
        transform: translateX(-50%);
        width: 30px;
        height: 2px;
        background: #e53e3e;
        border-radius: 1px;
        opacity: 0.3;
      }
    }
  }
}

// 空状态样式
.uc-empty-state {
  text-align: center;
  padding: 80px 32px;
  color: #64748b;

  i {
    font-size: 64px;
    color: #cbd5e1;
    margin-bottom: 20px;
    display: block;
  }

  p {
    font-size: 16px;
    margin: 0;
    font-weight: 500;
  }
}

// 订单列表样式
.uc-orders-list {
  padding: 24px;

  .uc-order-card {
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    margin-bottom: 20px;
    overflow: hidden;
    transition: all 0.3s ease;

    &:hover {
      border-color: #e53e3e;
      box-shadow: 0 4px 16px rgba(229, 62, 62, 0.1);
      transform: translateY(-2px);
    }

    .uc-order-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 16px 20px;
      background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
      border-bottom: 1px solid #e5e7eb;

      .uc-order-no {
        font-size: 14px;
        font-weight: 600;
        color: #1e293b;
      }

      .uc-order-status {
        padding: 6px 12px;
        border-radius: 20px;
        font-size: 12px;
        font-weight: 600;
        color: white;
      }
    }

    .uc-order-body {
      padding: 20px;
      display: flex;
      gap: 16px;
      align-items: flex-start;

      .uc-service-icon {
        width: 60px;
        height: 60px;
        border-radius: 8px;
        background: linear-gradient(135deg, #e53e3e 0%, #c53030 100%);
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        box-shadow: 0 2px 8px rgba(229, 62, 62, 0.2);

        i {
          font-size: 24px;
          color: white;
        }
      }

      .uc-order-details {
        flex: 1;

        .uc-order-title {
          font-weight: 600;
          color: #1e293b;
          margin: 0 0 8px 0;
          font-size: 16px;
          line-height: 1.4;
        }

        .uc-order-desc {
          color: #64748b;
          font-size: 14px;
          margin: 0 0 12px 0;
          line-height: 1.4;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .uc-order-meta {
          display: flex;
          gap: 16px;
          align-items: center;
          flex-wrap: wrap;
          margin-bottom: 8px;

          span {
            font-size: 13px;
            color: #64748b;
            padding: 2px 8px;
            background: #f1f5f9;
            border-radius: 4px;

            &.uc-order-amount {
              color: #e53e3e;
              font-weight: 600;
              font-size: 16px;
              background: rgba(229, 62, 62, 0.1);
            }

            &.uc-service-type {
              background: #e0f2fe;
              color: #0369a1;
            }

            &.uc-merchant {
              background: #f0fdf4;
              color: #166534;
            }
          }
        }

        .uc-appointment-info {
          display: flex;
          align-items: center;
          gap: 6px;
          color: #64748b;
          font-size: 13px;

          i {
            color: #e53e3e;
          }
        }

        .uc-appeal-info {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 13px;
          margin-top: 4px;

          .appeal-text {
            color: #f59e0b;
          }

          .appeal-result {
            color: #10b981;
          }

          i {
            &.el-icon-warning {
              color: #f59e0b;
            }
            &.el-icon-circle-check {
              color: #10b981;
            }
          }
        }

        .uc-refund-info {
          display: flex;
          align-items: center;
          gap: 6px;
          color: #ef4444;
          font-size: 13px;
          margin-top: 4px;

          i {
            color: #ef4444;
          }
        }
      }
    }

    .uc-order-footer {
      padding: 16px 20px;
      border-top: 1px solid #e5e7eb;
      background: #fafafa;

      .uc-order-time {
        font-size: 12px;
        color: #64748b;
      }
    }
  }
}

// 骨架屏动画
@keyframes uc-skeleton-loading {
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
}

.uc-skeleton {
  background: linear-gradient(90deg, #fed7d7 25%, #feb2b2 37%, #fed7d7 63%);
  background-size: 400px 100%;
  animation: uc-skeleton-loading 1.4s ease-in-out infinite;
  border-radius: 6px;
}

.uc-skeleton-loader {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;

  .uc-skeleton-avatar {
    width: 64px;
    height: 64px;
    border-radius: 50%;
  }

  .uc-skeleton-text {
    height: 16px;
    border-radius: 4px;

    &.uc-skeleton-text-lg {
      width: 120px;
      height: 20px;
    }

    &.uc-skeleton-text-md {
      width: 100px;
    }

    &.uc-skeleton-text-sm {
      width: 80px;
      height: 14px;
    }
  }
}

// 骨架屏样式
.uc-skeleton-orders {
  padding: 24px;

  .uc-skeleton-order-card {
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 20px;
    margin-bottom: 20px;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
}

// 分页组件样式
.uc-pagination {
  display: flex;
  justify-content: center;
  padding: 30px 24px;
  border-top: 1px solid #e5e7eb;
  background: #fafafa;

  .el-pagination {
    .el-pager li {
      background: #fff;
      border: 1px solid #e5e7eb;
      margin: 0 2px;

      &:hover {
        color: #e53e3e;
        border-color: #e53e3e;
      }

      &.active {
        background: #e53e3e;
        border-color: #e53e3e;
        color: #fff;
      }
    }

    .btn-prev,
    .btn-next {
      background: #fff;
      border: 1px solid #e5e7eb;

      &:hover {
        color: #e53e3e;
        border-color: #e53e3e;
      }

      &:disabled {
        color: #c0c4cc;
        border-color: #e5e7eb;
      }
    }

    .el-pagination__total {
      color: #666;
      font-weight: 500;
    }

    .el-pagination__jump {
      color: #666;

      .el-input__inner {
        border-color: #e5e7eb;

        &:focus {
          border-color: #e53e3e;
        }
      }
    }
  }
}

// 响应式设计
@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 1200px) {
  .user-center {
    grid-template-columns: 260px 1fr;
    gap: 18px;
    padding: 18px;
    max-width: 1000px;
  }
}

@media (max-width: 1024px) {
  .user-center {
    grid-template-columns: 1fr;
    gap: 20px;
    padding: 16px;
    max-width: 800px;
  }

  .uc-sidebar .uc-profile-section .uc-profile-content {
    display: flex;
    align-items: flex-start;
    gap: 20px;
    padding: 20px 16px;

    .uc-avatar-section {
      margin-bottom: 0;
      flex-shrink: 0;

      .uc-profile-avatar {
        width: 70px;
        height: 70px;
      }
    }

    .uc-info-section {
      flex: 1;
      gap: 0;

      .uc-info-item {
        padding: 10px 0;

        .uc-info-label {
          min-width: 70px;
          font-size: 12px;
        }

        .uc-info-value {
          font-size: 13px;
        }
      }
    }
  }
}

@media (max-width: 768px) {
  .user-center {
    padding: 16px;
    gap: 16px;
    max-width: 600px;
  }

  .uc-sidebar .uc-profile-section .uc-profile-content {
    flex-direction: column;
    text-align: center;
    gap: 16px;
    padding: 16px 12px;

    .uc-avatar-section .uc-profile-avatar {
      width: 60px;
      height: 60px;
    }

    .uc-info-section .uc-info-item {
      justify-content: center;
      flex-direction: column;
      text-align: center;
      gap: 4px;

      .uc-info-value {
        text-align: center;
        padding-left: 0;
      }
    }
  }

  .uc-tab-container {
    overflow-x: auto;

    .uc-tab-item {
      flex-shrink: 0;
      min-width: 80px;
      padding: 12px 16px;
      font-size: 13px;
    }
  }

  .uc-order-card {
    .uc-order-header {
      flex-direction: column;
      gap: 8px;
      text-align: center;
    }

    .uc-order-body {
      flex-direction: column;
      gap: 16px;
      align-items: center;
      text-align: center;

      .uc-service-icon {
        align-self: center;
      }
    }

    .uc-order-footer {
      text-align: center;
    }
  }
}

@media (max-width: 480px) {
  .user-center {
    padding: 12px;
    gap: 12px;
  }

  .uc-sidebar .uc-profile-section {
    .uc-profile-header {
      padding: 12px 16px;

      h2 {
        font-size: 15px;
      }
    }

    .uc-profile-content {
      padding: 16px 12px;

      .uc-avatar-section .uc-profile-avatar {
        width: 60px;
        height: 60px;
        border-width: 2px;
      }

      .uc-info-section .uc-info-item {
        padding: 8px 0;

        .uc-info-label {
          font-size: 11px;

          i {
            font-size: 14px;
            width: 16px;
          }
        }

        .uc-info-value {
          font-size: 12px;
        }
      }
    }
  }

  .uc-orders-list {
    padding: 12px;
  }

  .uc-order-card .uc-order-body {
    .uc-service-icon {
      width: 50px;
      height: 50px;

      i {
        font-size: 20px;
      }
    }
  }
}

/* 强制密码修改弹窗样式 */
.force-password-change-dialog {
  .el-dialog {
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  }

  .el-dialog__header {
    background: #e74c3c;
    color: #fff;
    padding: 24px;
    text-align: center;

    .el-dialog__title {
      color: #fff;
      font-weight: 500;
      font-size: 18px;
    }
  }

  .el-dialog__body {
    padding: 32px 24px 24px;
    background: #fff;
  }

  .el-dialog__footer {
    background: #fff;
    padding: 0 24px 24px;
    border-top: none;
  }
}

.password-change-header {
  text-align: center;
  margin-bottom: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  .el-icon-warning {
    color: #e74c3c;
    font-size: 20px;
    margin: 0;
  }

  .password-change-title {
    margin: 0;
    font-size: 14px;
    color: #666;
    line-height: 1.4;
  }

  &.user-id-notice {
    background: linear-gradient(
      135deg,
      rgba(231, 76, 60, 0.1) 0%,
      rgba(192, 57, 43, 0.05) 100%
    );
    border: 1px solid rgba(231, 76, 60, 0.2);
    border-radius: 8px;
    padding: 10px 16px;
    margin-bottom: 20px;

    .el-icon-warning {
      color: #e74c3c;
      font-size: 18px;
    }

    .password-change-title {
      color: #2c3e50;
      font-weight: 500;
      line-height: 1.6;
    }
  }
}

.password-change-form {
  .el-form-item {
    margin-bottom: 20px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .el-form-item__label {
    color: #333;
    font-weight: 500;
    font-size: 14px;
  }

  .el-input__inner {
    height: 44px;
    border-radius: 8px;
    border: 1px solid #e0e0e0;
    transition: all 0.2s ease;
    font-size: 14px;

    &:focus {
      border-color: #e74c3c;
      box-shadow: 0 0 0 3px rgba(231, 76, 60, 0.1);
    }

    &::placeholder {
      color: #999;
    }
  }

  .el-form-item.is-error .el-input__inner {
    border-color: #e74c3c;
  }

  .el-form-item__error {
    color: #e74c3c;
    font-size: 12px;
  }
}

.password-change-btn {
  width: auto;
  height: 44px;
  background: #e74c3c !important;
  border: none !important;
  border-radius: 8px;
  color: #fff !important;
  font-size: 15px;
  font-weight: 500;
  transition: all 0.2s ease;
  padding: 12px 24px;

  &:hover {
    background: #d62c1a !important;
    transform: translateY(-1px);
    box-shadow: 0 4px 16px rgba(231, 76, 60, 0.3);
  }

  &:active {
    transform: translateY(0);
    background: #c0392b !important;
  }

  &.is-loading {
    background: #e74c3c !important;
    cursor: not-allowed;
  }
}
</style>
